<template>
  <v-app
    id="container"
    :style="{ background: $vuetify.theme.themes[theme].background }"
  >
    <loader :is-loading="loading">
      <c-navigation-drawer
        :style="{
          background: $vuetify.theme.themes[theme].secondarybackground,
        }"
      />
      <c-right-drawer
        :style="{
          background: $vuetify.theme.themes[theme].secondarybackground,
        }"
        v-model="isCollapse"
      >
        <template v-slot:default>
          <router-view name="right-drawer" />
        </template>
      </c-right-drawer>
      <c-toolbar />
      <v-main>
        <v-container v-if="isInactive">
          <v-alert type="error" class="mx-n0" color="orange">
            <span>
              Votre compte GTF est actuellement désactivé. Pour le réactiver
              veuillez faire une demande sur MyPortal.
              <br />
              Sélectionnez l'application : Habilitation GTF (HAB GTF) et dans la
              Description du problème indiquez par exemple :
              <br />
              Merci de réactiver mon compte GTF svp
            </span>
            <template #append>
              <v-btn
                href="https://engiegbs.service-now.com/myportal?id=sc_cat_item&sys_id=62c890121b6f90941b9610229b4bcba5"
                target="_blank"
                elevation="8"
                depressed
                outilned
                color="warning"
              >
                MyPortal - Service Portal
                <v-icon>mdi-link</v-icon>
              </v-btn>
            </template>
          </v-alert>
        </v-container>
        <v-container text-xs-center v-if="!authenticated">
          <v-layout align-center justify-center row wrap>
            <v-flex xs6>
              <v-alert :value="true" color="error" icon="warning" outline>
                {{ generateMessage('boilerplate.disconnected_message') }}
              </v-alert>
            </v-flex>
          </v-layout>
        </v-container>
        <v-container fluid class="under-nav-bar-container">
          <div class="under-nav-bar">
            <router-view
              name="top"
              :style="{
                background: $vuetify.theme.themes[theme].secondarybackground,
              }"
            />
          </div>
          <router-view v-if="authenticated" :key="$route.fullPath" />
        </v-container>
      </v-main>
      <custom-boilerplate-modal>
        <router-view name="dialog" />
      </custom-boilerplate-modal>
      <v-fab-transition>
        <v-btn
          v-scroll="onScroll"
          v-show="fab"
          color="error"
          small
          fixed
          bottom
          right
          fab
          @click="toTop"
        >
          <v-icon>keyboard_arrow_up</v-icon>
        </v-btn>
      </v-fab-transition>
      <custom-store-dialog />
      <custom-snackbar />
      <custom-selected-currents />
    </loader>
  </v-app>
</template>

<script>
import CNavigationDrawer from '@/components/boilerplate/SideBar';
import CToolbar from '@/components/boilerplate/NavBar';
import { mapGetters } from 'vuex';
import { generateMessage } from '@/utils/i18n';
import CRightDrawer from '@/components/boilerplate/RightSideBar/CustomRightDrawer';
import CustomBoilerplateModal from '@/components/boilerplate/dialogs/CustomBoilerplateModal';
import CustomStoreDialog from '@/components/dialogs/storeModal/CustomStoreDialog';
import CustomSnackbar from '@/components/tools/CustomSnackbar';
import Loader from '@/components/widgets/Loader';
import AuthorizationsService from '@/services/services/authorization.service';

import CustomSelectedCurrents from '@/components/dialogs/CustomSelectedCurrents';
import { AUTHORIZATION_MODULE_PATH } from '@/store/modules/authorization';
import { PROPERTIES_LIST_ROUTE_NAME } from '@/router/properties.router';
import { ALERTS_LIST_ROUTE_NAME } from '@/router/alerts.router';
import { REQUESTS_LIST_ROUTE_NAME } from '@/router/requests.router';

export default {
  components: {
    Loader,
    CustomSnackbar,
    CustomStoreDialog,
    CustomBoilerplateModal,
    CustomSelectedCurrents,
    CRightDrawer,
    CNavigationDrawer,
    CToolbar,
  },
  data() {
    return {
      fab: false,
      loading: false,
      isInactive: false,
    };
  },
  async created() {
    this.loading = true;
    await this.$store.dispatch('boilerplate/initLocalPreference');
    try {
      const user = await this.$auth.getUser();
      await this.$store.dispatch('global/setUser', user);
      await this.$store.dispatch('boilerplate/setToken', null);
      const token = await this.$auth.getIdToken();
      await this.$store.dispatch('boilerplate/setToken', token);
      await AuthorizationsService.getItems().then((res) => {
        this.$store.dispatch('authorizations/setAuthorizations', res);
        this.isInactive = res.isInactive;
        if (this.$route.name === 'Root') {
          if (res.isDispatcher) {
            this.$router.replace({
              name: REQUESTS_LIST_ROUTE_NAME,
            });
          } else if (res.isPEM) {
            this.$router.replace({
              name: ALERTS_LIST_ROUTE_NAME,
            });
          } else {
            this.$router.replace({
              name: PROPERTIES_LIST_ROUTE_NAME,
            });
          }
        }
      });

      await this.$store.commit('boilerplate/SET_INIT', true);
      this.$router.beforeEach((to, from, next) => {
        const requiredProfiles = to.meta.authorizations;
        if (this.hasAccess(requiredProfiles)) {
          next();
        } else {
          this.$router.replace({
            name: 'error',
            params: {
              errorCode: '403 Forbidden',
              errorMessage: `Vous n'avez pas accès à cette page`,
            },
          });
        }
      });
    } catch (error) {
      if (error.code || error.message) {
        await this.$router.replace({
          name: 'error',
          params: {
            errorCode: error.code,
            errorMessage: error.message,
            errorStack: error.stack,
          },
        });
      } else if (error.xhr) {
        await this.$router.replace({
          name: 'error',
          params: {
            errorCode: JSON.parse(error.xhr.response).error,
            errorMessage: JSON.parse(error.xhr.response).error_description,
          },
        });
      }
    } finally {
      this.loading = false;
    }
  },
  computed: {
    ...mapGetters({
      darkThemeEnabled: 'boilerplate/darkThemeEnabled',
      sidebar: 'boilerplate/rightSidebar',
      authenticated: 'boilerplate/authenticated',
      hasAccess: `${AUTHORIZATION_MODULE_PATH}/hasAccess`,
    }),
    theme() {
      return this.$vuetify.theme.dark ? 'dark' : 'light';
    },
    isCollapse: {
      get() {
        return this.sidebar.opened;
      },
      set(val) {
        this.$store.dispatch('boilerplate/setRightSideBarOpened', val);
      },
    },
  },
  watch: {
    darkThemeEnabled() {
      this.$vuetify.theme.dark = this.darkThemeEnabled;
    },
  },
  methods: {
    generateMessage,
    onScroll(e) {
      if (typeof window === 'undefined') {
        return;
      }
      const top = window.pageYOffset || e.target.scrollTop || 0;
      this.fab = top > 20;
    },
    toTop() {
      this.$vuetify.goTo(0);
    },
  },
};
</script>

<style scoped>
.under-nav-bar-container {
  padding-top: 0 !important;
}
.under-nav-bar {
  position: -webkit-sticky;
  position: sticky;
  top: 55px;
  z-index: 1;
}
</style>
